/*
  Carnevore Mountain content page sections (Why Feed Raw). Loaded with defer; no libraries.
  - Fade-in below the fold: elements with data-cm-reveal that start off screen get .cm-rv, then .is-in once they
    scroll into view. Anything already on screen, any visitor with reduced motion, and any browser without
    IntersectionObserver sees the content as is. Nothing is hidden without this script.
    The homepage and shop scripts use the same attribute and the same guard (data-cm-reveal-seen), so an element is
    only ever handled once, whichever script runs first.
  - <cm-raw-bar>: the meal breakdown bar fills in once when it scrolls into view. Without scripts, with reduced
    motion, or when it is already on screen, it is simply full.
  Both re-initialise when the theme editor reloads a section (shopify:section:load).
*/
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canObserve = 'IntersectionObserver' in window;

  /* ---------- Fade in below the fold ---------- */
  let revealObserver = null;

  function reveal(root) {
    if (reducedMotion.matches || !canObserve) return;
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
    }
    root.querySelectorAll('[data-cm-reveal]').forEach((element) => {
      if (element.dataset.cmRevealSeen) return;
      element.dataset.cmRevealSeen = 'true';
      if (element.getBoundingClientRect().top < window.innerHeight) return; // already on screen: leave it be
      element.classList.add('cm-rv');
      revealObserver.observe(element);
    });
  }

  /* ---------- Meal breakdown bar ---------- */
  if (!customElements.get('cm-raw-bar')) {
    customElements.define(
      'cm-raw-bar',
      class CmRawBar extends HTMLElement {
        connectedCallback() {
          if (reducedMotion.matches || !canObserve) return;
          if (this.getBoundingClientRect().top < window.innerHeight) return;
          this.classList.add('is-pending');
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.classList.remove('is-pending');
              this.disconnectObserver();
            },
            { rootMargin: '0px 0px -10% 0px', threshold: 0.2 }
          );
          this.observer.observe(this);
        }

        disconnectedCallback() {
          this.disconnectObserver();
        }

        disconnectObserver() {
          if (this.observer) this.observer.disconnect();
          this.observer = null;
        }
      }
    );
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => reveal(document));
  else reveal(document);
  document.addEventListener('shopify:section:load', (event) => reveal(event.target));
})();
